import { useState } from "react";
import type { FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { dramaInfo } from "@/mocks/drama";

const FORM_URL = "https://readdy.ai/api/form/db73p8v799gd4v2febg0";

const missionItems = [
  {
    icon: "ri-focus-3-line",
    title: "网站定位",
    desc: "我们是一个聚焦影视剧《善良的小姨子》的专题内容站，围绕剧集提供在线观看入口、剧情介绍、演员资料与更新动态。",
  },
  {
    icon: "ri-heart-3-line",
    title: "我们的使命",
    desc: "用清晰的剧集信息与友好的观剧体验，让每一位剧迷都能快速找到想看的那一集，轻松追剧、安心观剧。",
  },
  {
    icon: "ri-shield-check-line",
    title: "内容与版权",
    desc: "本站在线播放、评论与留言均为前端演示效果，不提供真实视频源，所有剧集相关资料仅用于内容展示与学习交流。",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "联系与反馈",
    desc: "若你对网站功能、内容展示有任何建议，或遇到观看相关的问题，欢迎通过右侧表单给我们留言反馈。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "善良的小姨子-关于我们",
    description:
      "关于善良的小姨子在线观看网站：了解网站定位与使命、内容版权说明及联系方式，帮助用户更好地使用本站观剧与获取剧集信息。",
    keywords: "善良的小姨子网站介绍,关于我们,影视内容站,联系方式",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300";

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <span className="hover:text-primary-600 cursor-pointer">首页</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">关于我们</span>
        </nav>

        <SectionHeading
          eyebrow="关于本站"
          title="关于善良的小姨子在线观看"
          desc={`${dramaInfo.title} 专题内容站 · 专注剧集信息展示与观剧体验`}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {missionItems.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 shrink-0 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed mt-2">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-copyright-line text-accent-500"></i> 版权声明与隐私保护
          </h3>
          <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
            <p>
              本站为影视内容展示与信息聚合平台，所展示的剧集名称、剧情简介、演员资料等内容的版权归原作者及对应权利方所有。本站不存储、不提供任何真实视频文件，播放页面仅为前端演示效果。
            </p>
            <p>
              我们重视用户隐私，通过反馈表单提交的信息仅用于处理你的意见与问题，不会用于其他用途。如相关权利人认为本站内容不妥，欢迎通过下方表单联系我们，我们将及时处理。
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6 md:gap-8 mt-6">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">联系方式</h3>
            <ul className="space-y-3 text-sm text-foreground-700">
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                  <i className="ri-mail-line"></i>
                </span>
                zhuiju@example-drama.com
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                内容更新：每周三、周五 20:00
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                  <i className="ri-customer-service-line"></i>
                </span>
                反馈处理：1 - 3 个工作日
              </li>
            </ul>
            <div className="mt-5 rounded-lg bg-primary-50 border border-primary-100 p-4 text-xs text-primary-800 leading-relaxed">
              <i className="ri-information-line mr-1"></i>
              本站播放与留言均为演示功能，反馈表单提交后会展示感谢提示，请勿填写敏感个人信息。
            </div>
          </div>

          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-1">反馈与留言</h3>
            <p className="text-sm text-foreground-500 mb-5">告诉我们你的建议或遇到的问题，我们会认真阅读每一条留言。</p>

            {status === "success" ? (
              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-6 text-center">
                <i className="ri-checkbox-circle-fill text-3xl text-secondary-600"></i>
                <p className="font-heading text-base font-bold text-secondary-900 mt-3">感谢你的反馈！</p>
                <p className="text-sm text-secondary-800 mt-1.5">
                  我们已收到你的留言，会尽快处理。你的支持是我们持续优化的动力。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-secondary-500 text-secondary-950 text-sm font-medium hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line"></i> 再次留言
                </button>
              </div>
            ) : (
              <form data-readdy-form id="feedback-form" onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1.5">你的称呼</label>
                    <input type="text" name="name" required maxLength={50} placeholder="请输入称呼" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1.5">邮箱</label>
                    <input type="email" name="email" required placeholder="用于接收回复" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">反馈类型</label>
                  <select name="type" className={inputClass} defaultValue="内容建议">
                    <option value="内容建议">内容建议</option>
                    <option value="播放问题">播放问题</option>
                    <option value="版权相关">版权相关</option>
                    <option value="功能需求">功能需求</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">留言内容（500字以内）</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述你的建议或遇到的问题"
                    className={`${inputClass} resize-none`}
                  ></textarea>
                </div>

                {status === "error" && (
                  <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-send-plane-fill"></i>
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}